import type { ComponentProps } from 'react';

import type { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

/** The current normal-user People card width across legacy and migrated implementations. */
const PROFILE_CARD_WIDTH = 360;
const VIEWPORT_MARGIN = 16;
const POPUP_TO_CARD_GAP = 8;

type Rect = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left' | 'width' | 'height'>;

export type ContentStateInfoPopupCardPlacement = {
	position: NonNullable<ComponentProps<typeof ProfileCardWrapper>['position']>;
	offset: [number, number];
};

export const fallbackContentStateInfoPopupCardPlacement: ContentStateInfoPopupCardPlacement = {
	position: 'bottom-start',
	offset: [0, POPUP_TO_CARD_GAP],
};

const hasGeometry = (rect: Rect | null): rect is Rect =>
	Boolean(rect && rect.width > 0 && rect.height > 0);

/**
 * Calculates a physical, popup-relative placement for a card whose Popper reference is the user
 * trigger. The offset aligns the card to the content-state popup rather than the small trigger.
 */
export const getContentStateInfoPopupCardPlacement = ({
	popupRect,
	triggerRect,
	viewportWidth,
}: {
	popupRect: Rect | null;
	triggerRect: Rect | null;
	viewportWidth: number;
}): ContentStateInfoPopupCardPlacement => {
	if (!hasGeometry(popupRect) || !hasGeometry(triggerRect) || viewportWidth <= 0) {
		return fallbackContentStateInfoPopupCardPlacement;
	}

	if (popupRect.right + POPUP_TO_CARD_GAP + PROFILE_CARD_WIDTH <= viewportWidth - VIEWPORT_MARGIN) {
		return {
			position: 'right-start',
			offset: [
				popupRect.top - triggerRect.top,
				popupRect.right - triggerRect.right + POPUP_TO_CARD_GAP,
			],
		};
	}

	if (popupRect.left - POPUP_TO_CARD_GAP - PROFILE_CARD_WIDTH >= VIEWPORT_MARGIN) {
		return {
			position: 'left-start',
			offset: [
				popupRect.top - triggerRect.top,
				triggerRect.left - popupRect.left + POPUP_TO_CARD_GAP,
			],
		};
	}

	return {
		position: 'bottom-start',
		offset: [
			popupRect.left - triggerRect.left,
			popupRect.bottom - triggerRect.bottom + POPUP_TO_CARD_GAP,
		],
	};
};
